import { FunctionComponent, useRef, useState } from "react";
import { useOnMount, useOnUnmount } from "@pjs/react-utilities";
import { focusFirstInteractiveElement } from "@pjs/core-ui";
import { TemporaryAccessState } from "@pjs/user-domain";
import { temporaryAccessActions } from "../actions/TemporaryAccessActions.const";
import { ITemporaryAccessFormProps } from "./interfaces/ITemporaryAccessFormProps";
import { SelectReasonStep } from "./steps/SelectReasonStep.component";
import { SelectDurationStep } from "./steps/SelectDurationStep.component";
import { ConfirmationStep } from "./steps/ConfirmationStep.component";
import { SelectUserStep } from "./steps/SelectUserStep.component";
import { TemporaryAccessInstructions } from "./TemporaryAccessInstructions.component";
import { ITemporaryAccessFormModel } from "./interfaces/ITemporaryAccessFormModel";
import { InputType } from "./types/InputType";
import "./styles/temporary-access.css";

const getDefaultModel = (emails: ReadonlyArray<{ key: string; value: string }>): ITemporaryAccessFormModel => {
    if (emails.length === 1) {
        return {
            inputType: "email",
            admin: { valid: false, value: "" },
            email: { valid: true, value: emails[0].value },
            reason: { valid: false, value: "" }
        };
    }
    if (emails.length === 0) {
        return {
            inputType: "admin",
            admin: { valid: false, value: "" },
            email: { valid: false, value: "" },
            reason: { valid: false, value: "" }
        };
    }

    return {
        inputType: "email",
        admin: { valid: false, value: "" },
        email: { valid: false, value: "" },
        reason: { valid: false, value: "" }
    };
};

export const TemporaryAccessForm: FunctionComponent<ITemporaryAccessFormProps> = (props) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [formModel, setFormModel] = useState<ITemporaryAccessFormModel>(() => {
        return getDefaultModel(props.settings.emails);
    });

    const [duration, setDuration] = useState(props.settings.setDurationDays ?? 1);

    const onEmailChange = (email: string, valid: boolean): void => {
        setFormModel({ ...formModel, email: { value: email, valid: valid } });
    };

    const onReasonChange = (reason: string): void => {
        setFormModel({ ...formModel, reason: { value: reason, valid: reason.trim().length > 0 } });
    };

    const onInputTypeChange = (inputType: InputType): void => {
        setFormModel({ ...formModel, inputType: inputType });
    };

    const onAdminSelected = (adminId: string, itemIndex: number): void => {
        setFormModel({ ...formModel, admin: { value: adminId, valid: itemIndex !== 0 } });
    };

    useOnMount(() => {
        focusFirstInteractiveElement(containerRef.current);
    });

    useOnUnmount(() => {
        props.clearFormData();
    });

    return (
        <div ref={containerRef}>
            <TemporaryAccessInstructions supplementaryInformation={props.settings.description} />
            <div className="uui-temporary-access__step-container">
                <form onSubmit={(e) => e.preventDefault()}>
                    <SelectReasonStep onNewTextSet={onReasonChange} value={formModel.reason.value} />
                    <SelectUserStep
                        emails={props.settings.emails}
                        admins={props.admins}
                        activeAdminId={formModel.admin.value}
                        currentEmail={formModel.email.value}
                        onAdminChange={onAdminSelected}
                        onEmailChange={onEmailChange}
                        onInputTypeChange={onInputTypeChange}
                    />
                    <SelectDurationStep
                        onDurationChange={setDuration}
                        setDuration={props.settings.setDurationDays}
                        maxDuration={props.settings.maxDurationDays}
                    />
                    <ConfirmationStep
                        isSending={props.modelState.descriptor === TemporaryAccessState.GrantPending}
                        shouldBeActive={formModel.reason.valid && formModel[formModel.inputType].valid}
                        onClick={() => {
                            temporaryAccessActions.grantTemporaryAccess(
                                {
                                    reason: formModel.reason.value,
                                    days: duration,
                                    recipient: formModel[formModel.inputType].value
                                },
                                formModel.inputType === "admin"
                            );
                        }}
                    />
                </form>
            </div>
        </div>
    );
};
